import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const INFO_EMAIL = "info@gortonsuperstore.uk";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center text-slate-600 hover:text-emerald-600 mb-8 text-sm"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-8">
          Last updated: February 2026
        </p>

        <div className="prose prose-slate max-w-none space-y-5 text-slate-700 leading-relaxed">
          <p>
            Gorton Superstore (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is
            committed to protecting your privacy. This Privacy Policy explains how we
            collect, use, and protect your personal information when you visit
            www.gortonsuperstore.uk or place an order with us.
          </p>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              1. Information We Collect
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account &amp; order details:</strong> your name, email address,
                phone number, delivery / collection address and postcode.
              </li>
              <li>
                <strong>Payment information:</strong> processed securely by our payment
                provider (Stripe). We do not store your card details on our servers.
              </li>
              <li>
                <strong>Communications:</strong> the content of any emails, WhatsApp,
                telephone or contact-form messages you send us.
              </li>
              <li>
                <strong>Technical data:</strong> IP address, browser type, device
                information, and cookies to help the site function and improve our
                service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              2. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To process your orders and arrange collection or delivery.</li>
              <li>
                To send you order confirmations, delivery updates, and receipts by email
                and SMS.
              </li>
              <li>
                To respond to your enquiries at{" "}
                <a href={`mailto:${INFO_EMAIL}`} className="text-emerald-600 hover:underline">
                  {INFO_EMAIL}
                </a>
                .
              </li>
              <li>
                To improve our website, our product range, and our service through
                anonymised analytics.
              </li>
              <li>
                To comply with our legal obligations (including tax, accounting, and
                fraud prevention).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              3. Legal Basis (UK GDPR)
            </h2>
            <p>
              We process your personal data on the following legal bases:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Contract:</strong> when we need your information to fulfil an
                order you have placed with us.
              </li>
              <li>
                <strong>Legitimate interests:</strong> to run and improve our business,
                prevent fraud, and keep our site secure.
              </li>
              <li>
                <strong>Consent:</strong> where you have opted in to marketing emails or
                cookies that are not strictly necessary.
              </li>
              <li>
                <strong>Legal obligation:</strong> where we are required by law to
                retain records (for example, invoices for HMRC).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              4. Sharing Your Information
            </h2>
            <p>We only share your data with trusted third parties who help us run the store:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Payment processors</strong> (Stripe) to take card payments
                securely.
              </li>
              <li>
                <strong>Delivery service</strong> — our own Gorton Superstore Delivery
                Operations team, who deliver your Saturday orders across Manchester.
              </li>
              <li>
                <strong>Email &amp; SMS providers</strong> (Resend, Twilio) to send
                order confirmations and updates.
              </li>
              <li>
                <strong>Hosting &amp; analytics providers</strong> who process data on
                our behalf under contract.
              </li>
            </ul>
            <p>
              We do not sell your personal data to any third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              5. Data Retention
            </h2>
            <p>
              We keep your order and account information for as long as necessary to
              fulfil the purposes set out in this policy and to meet our legal, tax, and
              accounting obligations (typically up to 6 years from the date of your last
              purchase).
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              6. Cookies
            </h2>
            <p>
              Our website uses essential cookies to make it work (such as remembering
              your basket) and optional analytics cookies to help us understand how
              visitors use the site. You can control cookies through your browser
              settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              7. Your Rights
            </h2>
            <p>Under UK GDPR you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Ask us to correct information that is inaccurate.</li>
              <li>
                Ask us to delete your personal data (subject to legal retention
                requirements).
              </li>
              <li>Object to or restrict how we use your data.</li>
              <li>Withdraw consent for marketing at any time.</li>
              <li>
                Lodge a complaint with the UK Information Commissioner&apos;s Office
                (ICO) at{" "}
                <a
                  href="https://ico.org.uk"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ico.org.uk
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-8 mb-3">
              8. Security
            </h2>
            <p>
              We use appropriate technical and organisational measures to keep your data
              safe, including HTTPS encryption across the site and secure card
              processing via Stripe.
            </p>
          </section>

          <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mt-10">
            <h2 className="text-xl font-semibold text-emerald-900 mb-2 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Questions about your data?
            </h2>
            <p className="text-emerald-900 mb-0">
              Contact us at{" "}
              <a
                href={`mailto:${INFO_EMAIL}`}
                className="font-semibold text-emerald-700 hover:underline"
                data-testid="privacy-support-email"
              >
                {INFO_EMAIL}
              </a>{" "}
              and we&apos;ll respond within 30 days.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
